import React, { FC, useContext, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import { selectOrganization } from "go-security/services/organizations/selectors";
import LinkEntityModal from "../../../../../../../../../../components/AppLink/LinkEntityModal/LinkEntityModal";
import {
	ContactApi,
	LinkedApi,
	PurchaseApi,
	StockApi,
} from "../../../../../../../../../../services/Api/Organization/types";
import OrderSource from "../../../../components/OrderSource";
import { PurchaseContext } from "../../pages/Preview";

interface Props {
	purchase: PurchaseApi;
	onUpdate?: (data: PurchaseApi) => void;
	fileImportPreviewMode?: boolean;
}

const PurchaseBasicInfoCard: FC<Props> = ({ purchase, onUpdate, fileImportPreviewMode }) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const [isContactToLink, setIsContactToLink] = useState<boolean>(false);
	const [isStockToLink, setIsStockToLink] = useState(false);
	const displayConnectStockButton = purchase.linked_stock_id || purchase.channel_id;
	const history = useHistory();
	const { customFieldsConfig } = useContext(PurchaseContext);

	const drawChannel = () => {
		let name = purchase?.channel?.name;
		if (purchase?.external_provider) name += ` ${purchase.external_provider}`;
		if (purchase?.external_name) name += `: ${purchase.external_name}`;

		return (
			<span className="text-truncate" title={name}>
				{name ?? "-"}
			</span>
		);
	};

	const onUpdateLinkedContactWithLink = (linkedContact: LinkedApi) => {
		if (onUpdate) {
			if (linkedContact.skipped) {
				onUpdate({
					...purchase,
					contact: undefined,
				});
			} else {
				onUpdate({
					...purchase,
					contact: {
						...purchase?.contact,
						name: linkedContact.resource.name,
						id: linkedContact.resource.id,
					} as ContactApi,
				});
			}
		}
	};

	const onUpdateLinkedContactWithoutLink = (contact: ContactApi) => {
		onUpdate && onUpdate({ ...purchase, contact: { ...contact } });
	};

	const updateLinkedStockWithLink = (linkedStock: LinkedApi) => {
		if (onUpdate) {
			if (linkedStock.skipped) {
				onUpdate({
					...purchase,
					stock: undefined,
				});
			} else {
				onUpdate({
					...purchase,
					stock: {
						...purchase?.stock,
						name: linkedStock.resource_name,
						id: linkedStock.resource_id,
					} as StockApi,
				});
			}
		}
	};

	const updateLinkedStockWithoutLink = (stock: StockApi) => {
		if (onUpdate) {
			onUpdate({ ...purchase, stock: { ...stock } });
		}
	};

	const connectStockHandler = () => {
		if (!purchase.linked_stock_id && purchase.channel_id) {
			history.push(`/${organization.id}/apps/active/${purchase.channel_id}`);
		}
		if (purchase.linked_stock_id) {
			setIsStockToLink(true);
		}
	};

	const connectStockContent = displayConnectStockButton ? (
		<div className="d-flex flex-column">
			<span className="d-inline-block">
				<AlertSVG className="me-1" />
				<small className="text-danger">
					{purchase?.external_stock_name
						? t("modules.purchase.field.unconnected_stock.title")
						: t("modules.purchase.field.connect_stock_with_direction.title")}
				</small>
			</span>
			{!fileImportPreviewMode && (
				<div className="mt-1">
					<Button onClick={connectStockHandler}>{t("common.action.connect")}</Button>
				</div>
			)}
		</div>
	) : null;

	return (
		<Card className="card-box">
			<Card.Body>
				<h5 className="card-title">{t("common.word.basic_info")}</h5>
				<div className="row">
					<div className="col-md-3">
						<div className="card-body-item">
							<div className="item-info">
								<span className="label">{t("common.word.reference_number")}</span>
								<span>{purchase.reference_number}</span>
							</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="card-body-item">
							<div className="item-info">
								<span className="label">{t("modules.purchase.field.purchased_at.title")}</span>
								<span>{FormatDate(purchase.purchased_at)}</span>
							</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="card-body-item">
							<div className="item-info">
								<span className="label">{t("common.word.created_at", { ns: "lib" })}</span>
								<span>{FormatDate(purchase.created_at)}</span>
							</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="card-body-item text-truncate">
							<div className="item-info text-truncate">
								<span className="label">{t("common.word.contact")}</span>
								{purchase?.contact ? (
									<div className="text-truncate d-flex flex-column">
										<Link
											to={`/${organization.id}/contacts/${purchase?.contact?.id}`}
											className="text-truncate"
										>
											{purchase?.contact?.name}
										</Link>
										<small className="text-muted">
											{t("lib:common.word.tax_id_no.title")}: {purchase?.contact?.tax_id_no}
										</small>
									</div>
								) : (
									(purchase?.external_contact_name || purchase?.linked_contact_id) && (
										<div className="d-flex flex-column">
											<span className="d-inline-block mt-n1 ">
												<AlertSVG className="me-1" />
												<small className="text-danger">
													{t("modules.purchase.field.unconnected_contact.title")}
													{purchase?.external_contact_name && ":"}
												</small>
												{purchase?.external_contact_name && (
													<>
														<br />
														<small className="text-wrap text-danger">
															"{purchase?.external_contact_name}"
														</small>
													</>
												)}
											</span>
											{!fileImportPreviewMode && (
												<div className="mt-1">
													<Button onClick={() => setIsContactToLink(true)}>
														{t("common.action.connect")}
													</Button>
												</div>
											)}
										</div>
									)
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="row mt-3">
					<div className="col-md-3">
						<div className="card-body-item">
							<div className="item-info">
								<span className="label">{t("common.word.stock")}</span>
								{purchase?.stock ? <span>{purchase?.stock?.name}</span> : connectStockContent}
							</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="card-body-item text-truncate">
							<div className="item-info text-truncate">
								<span className="label">{t("common.word.channel")}</span>
								{drawChannel()}
							</div>
						</div>
					</div>
					<div className="col-md-3">
						<div className="card-body-item text-truncate">
							<div className="item-info text-truncate">
								<span className="label">{t("common.word.comment.title")}</span>
								<span className="text-truncate" title={purchase?.comment}>
									{purchase?.comment || "-"}
								</span>
							</div>
						</div>
					</div>
					{purchase?.source && (
						<div className="col-md-3">
							<div className="card-body-item text-truncate">
								<div className="item-info text-truncate">
									<span className="label">{t("lib:common.word.source")}</span>
									<OrderSource source={purchase.source} organization={organization} />
								</div>
							</div>
						</div>
					)}
					<div className="col-md-3">
						<div className="card-body-item text-truncate">
							<div className="item-info text-truncate">
								<span className="label">{t("common.word.created_by")}</span>
								<span className="text-truncate" title={purchase?.created_by}>
									{purchase.created_by || t("common.word.system")}
								</span>
							</div>
						</div>
					</div>
					{purchase.custom_fields.map((customField) => {
						const { id, slug, value } = customField;
						const customFieldConfig = customFieldsConfig?.find(
							(currentCustomField) => currentCustomField.slug === slug
						);
						let name = "";
						if (customFieldConfig) {
							name = customFieldConfig.name;
						}
						return (
							<div key={id} className="col-md-3 mt-3">
								<div className="card-body-item text-truncate">
									<div className="item-info text-truncate">
										<span className="label">{name}</span>
										<span className="text-truncate" title={value}>
											{value}
										</span>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</Card.Body>
			{isContactToLink && !fileImportPreviewMode && (
				<LinkEntityModal
					onHide={() => setIsContactToLink(false)}
					isShown={isContactToLink}
					onSuccess={
						purchase?.linked_contact?.id ? onUpdateLinkedContactWithLink : onUpdateLinkedContactWithoutLink
					}
					item={
						{
							...purchase?.linked_contact,
							external_name: purchase?.external_contact_name ?? "",
							resource_type: "CONTACT",
						} as LinkedApi
					}
					channel={purchase?.channel}
				/>
			)}
			<LinkEntityModal
				onHide={() => setIsStockToLink(false)}
				isShown={isStockToLink}
				onSuccess={purchase?.stock_id ? updateLinkedStockWithLink : updateLinkedStockWithoutLink}
				item={
					{
						...purchase?.linked_stock,
						external_name: purchase?.external_stock_name,
						id: Number(purchase?.linked_stock_id),
						resource_type: "STOCK",
					} as LinkedApi
				}
				channel={purchase?.channel}
			/>
		</Card>
	);
};

export default PurchaseBasicInfoCard;
